import { Avatar, Box, IconButton, Radio, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import { BpmBox, DateText, LabelDateBox } from "./styles";
import { LabelText, TimeBox, VersionDetailsBox, styles } from "./styles";
import { TrackVersionsProps, month } from "./types";
import { useContext, useEffect, useState } from "react";
import { useGetUser } from "../../hooks/useGetUser";
import { User } from "../../proto/api_pb";
import { NavLink } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { AuthContext } from "../../contexts";
import { LightTooltip } from "../LightToolTip/LightToolTip";
import {
  ENTERPRISE_ARTWORK_TEMPLATE,
  TEXT_COLOR,
  isEnterprise,
} from "../../theme";

export const TrackVersions: React.FC<TrackVersionsProps> = ({
  stopAllSongs,
  handlePlayPause,
  isPlaying,
  version,
  isSelected,
  selectedVersionId,
  handleChange,
  updateSelectedTrackVersion,
  handleOpenDeleteModal,
  allowDownload,
  shareToken,
}) => {
  const isSharedContentPage = window.location.pathname.includes("share");
  const { getUserResponse } = useGetUser(
    version.getOwnerId(),
    undefined,
    shareToken
  );
  const [audio] = useState(new Audio(version.getTrackUrl()));
  const [owner, setOwner] = useState<User>();
  const [checked, setChecked] = useState<boolean>(false);
  const { userId } = useContext(AuthContext);
  const date = new Date(version.getTimestamp() * 1000);
  const year = date.getFullYear();
  const mm = month[date.getMonth()];
  const dd = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const switchHandleChange = () => {
    setChecked(!checked);
    handleChange(version.getTimestamp());
    updateSelectedTrackVersion();
  };

  const downloadSong = async () => {
    try {
      const response = await fetch(version.getTrackUrl());
      const songBlob = await response.blob();
      const songUrl = URL.createObjectURL(songBlob);
      const link = document.createElement("a");
      link.href = songUrl;
      link.download = version.getLabel()!;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.log("Download Error");
      console.log(error);
    }
  };

  useEffect(() => {
    if (audio && audio.paused) audio.src = version.getTrackUrl();
  }, [audio, version]);

  useEffect(() => {
    if (selectedVersionId === version.getId()) setChecked(isSelected);
    setOwner(getUserResponse);
    if (audio) {
      isPlaying ? audio.play() : audio.pause();
    }
  }, [
    audio,
    isPlaying,
    getUserResponse,
    selectedVersionId,
    isSelected,
    version,
  ]);

  useEffect(() => {
    audio && audio.addEventListener("ended", () => stopAllSongs());

    return () => {
      audio && audio.removeEventListener("ended", () => stopAllSongs());
    };
  }, [audio, version, stopAllSongs]);

  useEffect(() => {
    return () => {
      audio.pause();
    };
  }, [audio, getUserResponse]);

  return (
    <>
      <VersionDetailsBox>
        <Box>
          <Tooltip
            title={`Set "${version.getLabel()}" as current track version`}
          >
            <Radio
              checked={isSelected}
              onChange={switchHandleChange}
              name="radio-buttons"
              inputProps={{ "aria-label": "A" }}
            />
          </Tooltip>
        </Box>
        {owner && (
          <NavLink
            key={owner.getUserId()}
            to={`/user-profile/${owner.getUsername()}`}
            style={styles.contributorNavLink}
          >
            <Tooltip
              title={`Uploaded by ${owner.getUsername()}`}
              placement="top"
            >
              <Avatar
                style={styles.contributorsPhotos}
                src={
                  owner.getProfilePicture()
                    ? owner.getProfilePicture()
                    : isEnterprise
                    ? ENTERPRISE_ARTWORK_TEMPLATE
                    : "/assets/images/logo512.png"
                }
                alt={owner.getUsername()}
              />
            </Tooltip>
          </NavLink>
        )}

        <LabelDateBox>
          <Tooltip title={version.getLabel()} placement="top">
            <LabelText noWrap>{version.getLabel()}</LabelText>
          </Tooltip>
          <DateText noWrap>{`${mm} ${dd} ${year}`}</DateText>
        </LabelDateBox>
        <TimeBox>{`${hours > 12 ? hours % 12 : hours}:${minutes}:${seconds} ${
          hours > 12 ? "PM" : "AM"
        }`}</TimeBox>
        <BpmBox>
          BPM:{" "}
          {version.getAudioMetadata()?.getBpm() !== 0
            ? version.getAudioMetadata()?.getBpm()
            : "---"}
        </BpmBox>
        <Box sx={styles.iconButtonsContainer}>
          <IconButton
            aria-label="play"
            size="small"
            onClick={() => handlePlayPause(version.getId())}
            sx={{ color: TEXT_COLOR }}
          >
            {isPlaying ? (
              <PauseIcon sx={styles.playbackIcons} />
            ) : (
              <PlayArrowIcon sx={styles.playbackIcons} />
            )}
          </IconButton>

          <IconButton
            aria-label="stop"
            size="small"
            onClick={() => stopAllSongs()}
            sx={{ color: TEXT_COLOR }}
          >
            <StopIcon sx={styles.playbackIcons} />
          </IconButton>
          {(!isSharedContentPage || (isSharedContentPage && allowDownload)) && (
            <IconButton
              sx={styles.downloadIconButton}
              aria-label="download"
              onClick={downloadSong}
            >
              <DownloadIcon />
            </IconButton>
          )}

          <LightTooltip
            enterTouchDelay={0}
            enterDelay={300}
            title={
              isSelected
                ? "Cannot delete the current version"
                : userId !== version.getOwnerId()
                ? "Only owner can delete the version"
                : ""
            }
            placement="top"
          >
            <span>
              <IconButton
                sx={styles.deleteIconButton}
                onClick={() => handleOpenDeleteModal(version.getId())}
                aria-label="delete"
                disabled={userId !== version.getOwnerId() || isSelected}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </LightTooltip>
        </Box>
      </VersionDetailsBox>
    </>
  );
};
